import { trackTmImported } from '@src/analytics/EventTracker';
import Modal from '@src/components/common/Modal';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { matchLanguageByName } from '@src/services/translationMemoryImport/importPreview';
import { useTranslationMemoryImportStore } from '@src/stores-v2/StoreContext';
import DocumentIcon from '@src/svgs/DocumentIcon';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import UploadIcon from '@src/svgs/UploadIcon';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import {
  baseLanguageOf,
  resolveDetectedLanguage,
} from '@src/utility/languageMatching';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled, { keyframes } from 'styled-components';

import CollectionPicker, {
  type CollectionPickerOption,
} from './CollectionPicker';
import {
  FormActions,
  GhostButton,
  PrimaryButton,
  Select,
  TextInput,
} from './formControls';

import type { LanguageRef } from '@src/repositories/TranslationMemoryEntryRepository';
import type {
  ColumnMapping,
  ImportPreview,
  ImportTarget,
} from '@src/services/translationMemoryImport/importPreview';
import type { DetectedLanguage } from '@src/services/TranslationMemoryImportService';
import type { AuthorRef } from '@src/stores-v2/TranslationMemoryStore';

interface Props {
  showcaseId: string;
  languages: LanguageRef[];
  collections: CollectionPickerOption[];
  defaultSourceLanguageId: string | null;
  /** The collection the target picker prefills to ('' = Default) — the one
   * currently selected in the Translation Memory view. */
  defaultCollectionId?: string;
  author: AuthorRef | null;
  /** A retained file to re-import — auto-inspected on open (OQ-b/DS6). */
  initialFile?: File | null;
  /** Create a collection inline; resolves the new id or an error message. */
  onCreateCollection: (
    name: string,
    description: string,
  ) => Promise<{ id?: string; error?: string }>;
  onClose: () => void;
}

const CONTENT_STYLE: CSSProperties = {
  width: 'min(960px, 95vw)',
  background: 'var(--surface)',
  borderRadius: 'var(--radius-modal, 12px)',
  padding: '24px',
};

type PreviewFilter = 'all' | 'created' | 'overwritten' | 'skipped' | 'invalid';

/** Info-icon copy for a column the detector couldn't place (PLAT-10900). */
const UNIDENTIFIED_TOOLTIP_ID = 'tm-import-language-unidentified';
const UNIDENTIFIED_HINT =
  "We couldn't identify this column's language — its text may be too short, mixed, or mostly names and numbers. Pick the language yourself; the import works either way.";
/** Info-icon tooltip carrying the detector's reasoning for an identified column. */
const IDENTIFIED_TOOLTIP_ID = 'tm-import-language-identified';

/** Label a mapping option with the header name plus its column position. */
const columnLabel = (header: string, index: number): string =>
  header ? `${header} (Column ${index + 1})` : `Column ${index + 1}`;

const ACCEPTED = '.csv,.tsv,.xlsx,.xls';

/**
 * Explicit "All languages" target. The target select starts unset (a disabled
 * "Select a language" placeholder) so an import always states its target
 * deliberately — when detection names a language the workspace doesn't have,
 * the file must not silently land as "applies to every language".
 */
const ALL_LANGUAGES_ID = '__all__';

type Step = 'file' | 'configure' | 'preview' | 'results';

/**
 * Single-file document import (PLAT-10848 / E9–E12). A four-step flow: choose a
 * file → configure the language pair, target collections, and column mapping
 * (auto-detected, editable — DS5) → preview the created/overwritten/skipped/
 * invalid classification → confirm → a persistent results screen. All parsing is
 * client-side; the preview diffs against the cached entries (DS4); on confirm the
 * file is retained as a `translation_memory_import` and entries are written with
 * `origin=import` (E11/E12). A blank translation cell is a do-not-translate row
 * (DS10).
 */
const ImportModal = ({
  showcaseId,
  languages,
  collections,
  defaultSourceLanguageId,
  defaultCollectionId,
  author,
  initialFile,
  onCreateCollection,
  onClose,
}: Props) => {
  const importStore = useTranslationMemoryImportStore();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('file');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [mapping, setMapping] = useState<ColumnMapping>({
    sourceTerm: 0,
    translation: 1,
    comment: null,
  });
  const [sourceLanguageId, setSourceLanguageId] = useState(
    defaultSourceLanguageId ?? languages[0]?.id ?? '',
  );
  // '' = nothing picked yet (blocks Preview), ALL_LANGUAGES_ID = every language,
  // otherwise a specific supported language.
  const [translationLanguageId, setTranslationLanguageId] = useState('');
  // A single target collection ('' = Default/uncategorized). Import targets one
  // collection; the plumbing still carries an array (0 or 1) so the service /
  // preview fan-out is unchanged.
  const [collectionId, setCollectionId] = useState(defaultCollectionId ?? '');
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Detected languages (code + justification) for the mapped source/translation
  // columns (PLAT-10900); compared against the picked languages to surface a
  // non-blocking mismatch.
  const [detected, setDetected] = useState<{
    source: DetectedLanguage | null;
    translation: DetectedLanguage | null;
  } | null>(null);
  // True while a language-detection request is in flight (PLAT-10900), so the
  // Configure step can show a "Detecting language…" indicator.
  const [detecting, setDetecting] = useState(false);

  const buildTarget = (): ImportTarget | null => {
    const sourceLanguage = languages.find(l => l.id === sourceLanguageId);
    if (!sourceLanguage) return null;
    return {
      sourceLanguage,
      // `null` ⇒ the entries apply to every target language. Reached only via
      // the explicit "All languages" option — an unpicked target can't get
      // here (Preview is disabled until something is chosen).
      translationLanguage:
        translationLanguageId === ALL_LANGUAGES_ID
          ? null
          : (languages.find(l => l.id === translationLanguageId) ?? null),
      collectionIds: collectionId ? [collectionId] : [],
    };
  };

  const addCollection = async () => {
    const name = newName.trim();
    if (!name || creatingCollection) return;
    setCreatingCollection(true);
    setError(null);
    const result = await onCreateCollection(name, newDescription.trim());
    setCreatingCollection(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    const newId = result.id;
    if (newId) {
      setCollectionId(newId);
      setNewName('');
      setNewDescription('');
      setShowNewCollection(false);
    }
  };

  const collectionOptions: CollectionPickerOption[] = [
    { id: '', name: 'Default', description: 'Uncategorized' },
    ...collections,
  ];

  const onFileChosen = async (chosen: File) => {
    setBusy(true);
    setError(null);
    const result = await importStore.inspectFile(chosen);
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message || "Couldn't read that file.");
      return;
    }
    setFile(chosen);
    const { headers: cols, suggestedMapping } = result.value;
    setHeaders(cols);
    setRowCount(result.value.rowCount);
    setMapping(suggestedMapping);
    // Fuzzy pre-select the languages from the mapped column headers (e.g. a
    // "Spanish" column → Spanish target), so a header that names a supported
    // language is honored instead of leaving the default.
    const srcMatch = matchLanguageByName(
      cols[suggestedMapping.sourceTerm],
      languages,
      defaultSourceLanguageId,
    );
    if (srcMatch) setSourceLanguageId(srcMatch.id);
    const tgtMatch =
      suggestedMapping.translation != null
        ? matchLanguageByName(cols[suggestedMapping.translation], languages)
        : null;
    if (tgtMatch) setTranslationLanguageId(tgtMatch.id);
    setStep('configure');
  };

  // Re-import: auto-inspect the retained file once on open (DS6).
  const inspectedInitial = useRef(false);
  useEffect(() => {
    if (initialFile && !inspectedInitial.current) {
      inspectedInitial.current = true;
      void onFileChosen(initialFile);
    }
  }, [initialFile]);

  // Detect the source/translation column languages whenever the file or those
  // mapped columns change (PLAT-10900). Non-blocking — a failed detect just
  // leaves `detected` null (no warning). On a new file, the detected translation
  // language is pre-selected as the target (primary), once per file.
  const autoTargetFileRef = useRef<File | null>(null);
  // Read through refs so the effect depends only on what should actually
  // re-trigger detection (the file + the mapped columns). `languages` is an
  // array prop: listing it would re-fire a network detect — and a
  // `setDetected(null)` / `setDetecting(true)` render — on every identity
  // change of that array.
  const languagesRef = useRef(languages);
  languagesRef.current = languages;
  const defaultSourceLanguageIdRef = useRef(defaultSourceLanguageId);
  defaultSourceLanguageIdRef.current = defaultSourceLanguageId;
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    const controller = new AbortController();
    const isNewFile = autoTargetFileRef.current !== file;
    setDetected(null);
    setDetecting(true);
    void importStore
      .detectLanguages(
        file,
        {
          sourceTerm: mapping.sourceTerm,
          translation: mapping.translation,
          comment: null,
        },
        controller.signal,
      )
      .then(res => {
        if (cancelled) return;
        setDetecting(false);
        if (!res.ok) return;
        setDetected(res.value);
        // Latch on the first *resolved* detect for this file, not on the first
        // one that happened to name a target. Latching only on success let a
        // re-detect (the user remapping the translation column) still count as
        // "new file" and overwrite a target the user had picked by hand.
        if (isNewFile) {
          autoTargetFileRef.current = file;
          const target = res.value.translation;
          const match = target
            ? resolveDetectedLanguage(target.code, languagesRef.current, [
                defaultSourceLanguageIdRef.current,
              ])
            : null;
          if (match) setTranslationLanguageId(match.id);
        }
      })
      // The store returns a Result rather than rejecting, but a thrown error
      // must not strand the field on "Detecting language…" forever.
      .catch(() => {
        if (!cancelled) setDetecting(false);
      });
    return () => {
      cancelled = true;
      // Detection is a paid LLM call; abandon the in-flight one rather than
      // leaving it to complete into a discarded result.
      controller.abort();
    };
  }, [file, importStore, mapping.sourceTerm, mapping.translation]);

  const runPreview = async () => {
    const target = buildTarget();
    if (!file || !target) return;
    setBusy(true);
    setError(null);
    const result = await importStore.buildPreview(
      showcaseId,
      file,
      mapping,
      target,
    );
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message || "Couldn't read that file.");
      return;
    }
    setPreview(result.value);
    setStep('preview');
  };

  const confirmImport = async () => {
    const target = buildTarget();
    if (!file || !preview || !target) return;
    setBusy(true);
    setError(null);
    const result = await importStore.runImport({
      showcaseId,
      file,
      preview,
      target,
      author,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message || 'Import failed.');
      return;
    }
    // One event per import, carrying the ACTUAL write counts (PLAT-10871), not
    // the preview's optimistic tally. `file_count` is 1 on the single-file flow;
    // bulk import (PLAT-10849) will fill it in (PLAT-10853).
    trackTmImported({
      showcase_id: showcaseId,
      file_count: 1,
      row_count: preview.rows.length,
      created: result.value.counts.created,
      overwritten: result.value.counts.overwritten,
      skipped: result.value.counts.skipped,
      failed: result.value.counts.failed,
      invalid_rows: preview.invalidRows,
      source_lang: target.sourceLanguage.value ?? undefined,
      target_lang: target.translationLanguage?.value ?? null,
      // An empty target list is the single Default fan-out, not zero writes.
      collection_count: target.collectionIds.length || 1,
    });
    // Reflect the actual write counts on the results screen.
    setPreview({ ...preview, counts: result.value.counts });
    setStep('results');
  };

  const langLabel = (l: LanguageRef) => l.caption ?? l.value ?? l.id;
  const targetCollectionName =
    collectionOptions.find(o => o.id === collectionId)?.name ?? 'Default';
  const validRows = preview ? preview.rows.filter(r => !r.error).length : 0;
  const canConfirm =
    !!preview && preview.fileErrors.length === 0 && validRows > 0;

  // Language-mismatch derivation (PLAT-10900): compare each detected code
  // against the picked language at BASE-language granularity (ignore region).
  // Derived each render, so picking the detected language clears the warning.
  const selectedSource = languages.find(l => l.id === sourceLanguageId) ?? null;
  const selectedTarget =
    languages.find(l => l.id === translationLanguageId) ?? null;
  /**
   * A supported language whose base matches the detected code, if any. Regional
   * variants (English (US) / English (UK)) resolve to the language already
   * picked for that field, else the brand kit's primary — never to whichever
   * region the detector happened to guess.
   */
  const matchDetected = (
    code: string,
    selectedId?: string | null,
  ): LanguageRef | null =>
    resolveDetectedLanguage(code, languages, [
      selectedId,
      defaultSourceLanguageId,
    ]);
  const detectedName = (code: string, selectedId?: string | null): string => {
    const match = matchDetected(code, selectedId);
    return match ? langLabel(match) : formatLanguageCaption(code);
  };
  const sourceMismatch =
    detected?.source &&
    selectedSource?.value &&
    baseLanguageOf(detected.source.code) !==
      baseLanguageOf(selectedSource.value)
      ? detected.source
      : null;
  // Only validate the target when a specific target language is chosen ("All
  // languages" has nothing to disagree with).
  const targetMismatch =
    detected?.translation &&
    selectedTarget?.value &&
    baseLanguageOf(detected.translation.code) !==
      baseLanguageOf(selectedTarget.value)
      ? detected.translation
      : null;
  // Inline "we couldn't tell" note beside a language label, with an info icon
  // explaining what to do about it. Advisory only — the import is unaffected.
  const renderUnidentifiedHint = () => (
    <DetectedHint role="status">
      Language not identified
      <InfoAnchor
        data-tooltip-id={UNIDENTIFIED_TOOLTIP_ID}
        data-tooltip-content={UNIDENTIFIED_HINT}
        aria-label="Why the language wasn't identified"
      >
        <InfoIconSmall />
      </InfoAnchor>
    </DetectedHint>
  );

  // Resolved positive hint: grey "Identified <language>" with an info icon whose
  // tooltip carries the detector's reasoning (only when a reason is available).
  const renderIdentifiedHint = (d: DetectedLanguage, selectedId: string) => (
    <DetectedHint role="status">
      Identified {detectedName(d.code, selectedId)}
      {d.reason && (
        <InfoAnchor
          data-tooltip-id={IDENTIFIED_TOOLTIP_ID}
          data-tooltip-content={d.reason}
          aria-label={`Why we identified ${detectedName(d.code, selectedId)}`}
        >
          <InfoIconSmall />
        </InfoAnchor>
      )}
    </DetectedHint>
  );

  // Inline hint under a language Select (with the model's justification): the
  // detected language is supported but isn't the one picked, so offer a
  // one-click "Use <detected>".
  const renderMismatchHint = (
    d: DetectedLanguage,
    onUse: (id: string) => void,
  ) => {
    const match = matchDetected(d.code);
    if (!match) return null;
    return (
      <WarnLine role="status">
        <span>
          Looks like <strong>{detectedName(d.code)}</strong>
          {d.reason ? `. ${d.reason}` : ''}
        </span>
        <button type="button" onClick={() => onUse(match.id)}>
          Use {detectedName(d.code)}
        </button>
      </WarnLine>
    );
  };

  // The detected language isn't in the workspace, so there is nothing to select.
  // Sits in the detection slot beside the field label (same line as "Identified
  // <language>"), in warning gold, with an "Add" link to Brand Kit.
  const renderUnavailableHint = (d: DetectedLanguage) => (
    <UnavailableHint role="status">
      Identified {detectedName(d.code)} — Not Available
      {d.reason && (
        <InfoAnchor
          data-tooltip-id={IDENTIFIED_TOOLTIP_ID}
          data-tooltip-content={d.reason}
          aria-label={`Why we identified ${detectedName(d.code)}`}
        >
          <InfoIconSmall />
        </InfoAnchor>
      )}
      <button
        type="button"
        onClick={() => {
          onClose();
          navigate('/brand-kit#languages');
        }}
      >
        Add
      </button>
    </UnavailableHint>
  );

  /**
   * The one detection note that sits beside a language label (PLAT-10900).
   * Shared by both columns so the source and target slots can't drift apart.
   * Precedence: in-flight → unavailable → unidentified → identified. A
   * disagreement yields nothing here, because the "Use <detected>" line under
   * the Select owns that message and two notes would contradict each other.
   * Stays silent when the request itself failed (`detected` null), rather than
   * claiming "not identified".
   */
  const renderDetectionSlot = (
    d: DetectedLanguage | null,
    selected: LanguageRef | null,
    columnMapped: boolean,
  ) => {
    if (detecting)
      return (
        <DetectingHint>
          Detecting language
          <Dots />
        </DetectingHint>
      );
    if (!detected) return null;
    if (!d) return columnMapped ? renderUnidentifiedHint() : null;
    // Detected a language the workspace doesn't have: nothing to select, so the
    // only way forward is adding it in Brand Kit. Independent of the pick — the
    // target warns even on its "Select a language" placeholder, so an
    // unavailable language can't slip by as "All languages".
    if (!matchDetected(d.code)) return renderUnavailableHint(d);
    if (
      selected?.value &&
      baseLanguageOf(d.code) !== baseLanguageOf(selected.value)
    )
      return null;
    return renderIdentifiedHint(d, selected?.id ?? '');
  };

  return (
    <Modal isOpen closeModal={onClose} contentStyle={CONTENT_STYLE}>
      <Wrap>
        <h2>Import glossary</h2>

        {step === 'file' && (
          <DropArea
            htmlFor="tm-import-file"
            $dragging={dragging}
            onDragOver={e => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => {
              e.preventDefault();
              setDragging(false);
              const chosen = e.dataTransfer.files?.[0];
              if (chosen) void onFileChosen(chosen);
            }}
          >
            <HiddenInput
              id="tm-import-file"
              type="file"
              accept={ACCEPTED}
              onChange={e => {
                const chosen = e.target.files?.[0];
                if (chosen) void onFileChosen(chosen);
              }}
            />
            <IconCircle>
              <UploadIcon width="26" height="26" />
            </IconCircle>
            <strong>Drag &amp; drop a file here, or click to browse</strong>
            <span>CSV, TSV, or Excel (.csv, .tsv, .xlsx, .xls)</span>
            {busy && <Muted>Reading file…</Muted>}
          </DropArea>
        )}

        {step === 'configure' && (
          <>
            <FileLine>
              <span className="file">
                <DocumentIcon width="15" height="17" />
                <strong>{file?.name}</strong>
              </span>
              <span className="rows">{rowCount.toLocaleString()} rows</span>
            </FileLine>

            <Grid>
              <Field>
                <LabelRow>
                  <label>Source language</label>
                  {renderDetectionSlot(
                    detected?.source ?? null,
                    selectedSource,
                    true,
                  )}
                </LabelRow>
                <Select
                  value={sourceLanguageId}
                  onChange={e => setSourceLanguageId(e.target.value)}
                >
                  <option value="" disabled>
                    Select a language
                  </option>
                  {languages.map(l => (
                    <option key={l.id} value={l.id}>
                      {langLabel(l)}
                    </option>
                  ))}
                </Select>
                {sourceMismatch &&
                  renderMismatchHint(sourceMismatch, setSourceLanguageId)}
              </Field>
              <Field>
                <LabelRow>
                  <label>Target language</label>
                  {renderDetectionSlot(
                    detected?.translation ?? null,
                    selectedTarget,
                    mapping.translation != null,
                  )}
                </LabelRow>
                <Select
                  value={translationLanguageId}
                  onChange={e => setTranslationLanguageId(e.target.value)}
                >
                  <option value="" disabled>
                    Select a language
                  </option>
                  <option value={ALL_LANGUAGES_ID}>All languages</option>
                  {languages.map(l => (
                    <option key={l.id} value={l.id}>
                      {langLabel(l)}
                    </option>
                  ))}
                </Select>
                {targetMismatch &&
                  renderMismatchHint(targetMismatch, setTranslationLanguageId)}
              </Field>
            </Grid>
            {/* Rendered unconditionally — react-tooltip is inert without a
                matching anchor, so gating these on the current detection state
                only duplicated that state. The second is shared by the
                "Identified …" and "… — Not Available" hints. */}
            <Tooltip
              id={UNIDENTIFIED_TOOLTIP_ID}
              style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '280px' }}
            />
            <Tooltip
              id={IDENTIFIED_TOOLTIP_ID}
              style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '280px' }}
            />

            <Field>
              <div className="labelRow">
                <label>Target collection</label>
                {!showNewCollection && (
                  <LinkButton
                    type="button"
                    onClick={() => setShowNewCollection(true)}
                  >
                    + New Collection
                  </LinkButton>
                )}
              </div>
              {showNewCollection ? (
                <NewCollectionBox>
                  <TextInput
                    autoFocus
                    value={newName}
                    placeholder="Collection name"
                    onChange={e => setNewName(e.target.value)}
                  />
                  <TextInput
                    value={newDescription}
                    placeholder="Description (optional)"
                    onChange={e => setNewDescription(e.target.value)}
                  />
                  <FormActions>
                    <GhostButton
                      type="button"
                      onClick={() => {
                        setShowNewCollection(false);
                        setNewName('');
                        setNewDescription('');
                      }}
                      disabled={creatingCollection}
                    >
                      Cancel
                    </GhostButton>
                    <PrimaryButton
                      type="button"
                      onClick={addCollection}
                      disabled={!newName.trim() || creatingCollection}
                    >
                      {creatingCollection ? 'Adding…' : 'Add collection'}
                    </PrimaryButton>
                  </FormActions>
                </NewCollectionBox>
              ) : (
                <CollectionPicker
                  options={collectionOptions}
                  selectedId={collectionId}
                  onSelect={setCollectionId}
                />
              )}
            </Field>

            <Grid $cols={3}>
              <Field>
                <label>Source term column</label>
                <Select
                  value={mapping.sourceTerm}
                  onChange={e =>
                    setMapping(m => ({
                      ...m,
                      sourceTerm: Number(e.target.value),
                    }))
                  }
                >
                  {headers.map((h, i) => (
                    <option key={i} value={i}>
                      {columnLabel(h, i)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <label>Translation column</label>
                <Select
                  value={mapping.translation ?? -1}
                  onChange={e =>
                    setMapping(m => ({
                      ...m,
                      translation:
                        Number(e.target.value) < 0
                          ? null
                          : Number(e.target.value),
                    }))
                  }
                >
                  <option value={-1}>None (do not translate)</option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>
                      {columnLabel(h, i)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <label>Comment column</label>
                <Select
                  value={mapping.comment ?? -1}
                  onChange={e =>
                    setMapping(m => ({
                      ...m,
                      comment:
                        Number(e.target.value) < 0
                          ? null
                          : Number(e.target.value),
                    }))
                  }
                >
                  <option value={-1}>None</option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>
                      {columnLabel(h, i)}
                    </option>
                  ))}
                </Select>
              </Field>
            </Grid>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            {(sourceMismatch || targetMismatch) && (
              <WarnBox role="status">
                {sourceMismatch && (
                  <div>
                    Source column looks like{' '}
                    <strong>{detectedName(sourceMismatch.code)}</strong>
                    {sourceMismatch.reason ? ` (${sourceMismatch.reason})` : ''}
                    , but you chose{' '}
                    {selectedSource ? langLabel(selectedSource) : '—'}.
                  </div>
                )}
                {targetMismatch && (
                  <div>
                    Translation column looks like{' '}
                    <strong>{detectedName(targetMismatch.code)}</strong>
                    {targetMismatch.reason ? ` (${targetMismatch.reason})` : ''}
                    , but you chose{' '}
                    {selectedTarget ? langLabel(selectedTarget) : '—'}.
                  </div>
                )}
                <button type="button" onClick={() => setStep('configure')}>
                  Change language
                </button>
              </WarnBox>
            )}
            <PreviewBody
              preview={preview}
              collectionName={targetCollectionName}
            />
          </>
        )}

        {step === 'results' && preview && (
          <ResultsBody preview={preview} fileName={file?.name ?? ''} />
        )}

        {error && <ErrorText role="alert">{error}</ErrorText>}

        <FormActions>
          {step === 'results' ? (
            <PrimaryButton type="button" onClick={onClose}>
              Done
            </PrimaryButton>
          ) : (
            <>
              <GhostButton type="button" onClick={onClose} disabled={busy}>
                Cancel
              </GhostButton>
              {step === 'configure' && (
                <PrimaryButton
                  type="button"
                  onClick={runPreview}
                  disabled={busy || !sourceLanguageId || !translationLanguageId}
                >
                  {busy ? 'Reading…' : 'Preview'}
                </PrimaryButton>
              )}
              {step === 'preview' && (
                <>
                  <GhostButton
                    type="button"
                    onClick={() => setStep('configure')}
                    disabled={busy}
                  >
                    Back
                  </GhostButton>
                  <PrimaryButton
                    type="button"
                    onClick={confirmImport}
                    disabled={busy || !canConfirm}
                  >
                    {busy ? 'Importing…' : `Import ${validRows} row(s)`}
                  </PrimaryButton>
                </>
              )}
            </>
          )}
        </FormActions>
      </Wrap>
    </Modal>
  );
};

export default ImportModal;

/** Preview body: file errors + counts + a searchable/filterable classification. */
const PreviewBody = ({
  preview,
  collectionName,
}: {
  preview: ImportPreview;
  collectionName: string;
}) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<PreviewFilter>('all');

  const query = q.trim().toLowerCase();
  const rows = preview.rows.filter(row => {
    if (
      query &&
      !`${row.sourceTerm} ${row.translation} ${row.comment}`
        .toLowerCase()
        .includes(query)
    )
      return false;
    if (filter === 'all') return true;
    if (filter === 'invalid') return !!row.error;
    if (row.error) return false;
    return row.outcomes.some(o => o.action === filter);
  });

  return (
    <>
      <TargetLine>
        Importing into <strong>{collectionName}</strong>
      </TargetLine>
      {preview.fileErrors.length > 0 && (
        <ErrorBox role="alert">
          <strong>This file can&apos;t be imported:</strong>
          <ul>
            {preview.fileErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </ErrorBox>
      )}
      <PreviewToolbar>
        <TextInput
          type="search"
          value={q}
          placeholder="Search terms, translations, comments"
          onChange={e => setQ(e.target.value)}
        />
        <Select
          value={filter}
          onChange={e => {
            const v = e.target.value;
            setFilter(
              v === 'created' ||
                v === 'overwritten' ||
                v === 'skipped' ||
                v === 'invalid'
                ? v
                : 'all',
            );
          }}
        >
          <option value="all">All rows ({preview.rows.length})</option>
          <option value="created">New ({preview.counts.created})</option>
          <option value="overwritten">
            Overwrite ({preview.counts.overwritten})
          </option>
          <option value="skipped">Unchanged ({preview.counts.skipped})</option>
          {preview.invalidRows > 0 && (
            <option value="invalid">Invalid ({preview.invalidRows})</option>
          )}
        </Select>
      </PreviewToolbar>
      <RowScroll>
        <PreviewTable>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '32%' }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '28%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Source term</th>
              <th>Translation</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="empty" colSpan={4}>
                  No rows match your search or filter.
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr key={row.rowNumber}>
                  <td className="num">{row.rowNumber}</td>
                  <td>{row.sourceTerm || <Muted>—</Muted>}</td>
                  <td>
                    {row.dnt ? <em>Do not translate</em> : row.translation}
                  </td>
                  <td>
                    {row.error ? (
                      <Chip $tone="invalid">{row.error}</Chip>
                    ) : (
                      <RowActions>
                        {summarizeOutcomes(row.outcomes).map((label, i) => (
                          <Chip key={i} $tone={label.tone}>
                            {label.text}
                          </Chip>
                        ))}
                      </RowActions>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </PreviewTable>
      </RowScroll>
    </>
  );
};

/** Results body: final counts + retained errors that persist after completion. */
const ResultsBody = ({
  preview,
  fileName,
}: {
  preview: ImportPreview;
  fileName: string;
}) => {
  const invalid = preview.rows.filter(r => r.error);
  // Rows the write dropped (PLAT-10871) — reported, not retried. The rest of the
  // import did land, so this is a partial success, not a failure.
  const failed = preview.counts.failed;
  return (
    <>
      <Muted>Imported {fileName}.</Muted>
      <CountRow>
        <Chip $tone="created">{preview.counts.created} created</Chip>
        <Chip $tone="overwritten">
          {preview.counts.overwritten} overwritten
        </Chip>
        <Chip $tone="skipped">{preview.counts.skipped} unchanged</Chip>
        {invalid.length > 0 && (
          <Chip $tone="invalid">{invalid.length} skipped (invalid)</Chip>
        )}
        {failed > 0 && <Chip $tone="invalid">{failed} failed to write</Chip>}
      </CountRow>
      {failed > 0 && (
        <Muted>
          Some rows couldn&apos;t be saved. Re-import the file to retry just
          those — rows that already landed will show as unchanged.
        </Muted>
      )}
      {invalid.length > 0 && (
        <RowScroll>
          <PreviewTable>
            <thead>
              <tr>
                <th>#</th>
                <th>Source term</th>
                <th>Problem</th>
              </tr>
            </thead>
            <tbody>
              {invalid.map(row => (
                <tr key={row.rowNumber}>
                  <td className="num">{row.rowNumber}</td>
                  <td>{row.sourceTerm || <Muted>—</Muted>}</td>
                  <td>{row.error}</td>
                </tr>
              ))}
            </tbody>
          </PreviewTable>
        </RowScroll>
      )}
    </>
  );
};

type ChipTone = 'created' | 'overwritten' | 'skipped' | 'invalid';

/** Collapse per-collection outcomes into count chips (e.g. "2 new, 1 overwrite"). */
const summarizeOutcomes = (
  outcomes: ImportPreview['rows'][number]['outcomes'],
): { text: string; tone: ChipTone }[] => {
  const tally = { created: 0, overwritten: 0, skipped: 0 };
  for (const o of outcomes) tally[o.action] += 1;
  const chips: { text: string; tone: ChipTone }[] = [];
  if (tally.created)
    chips.push({ text: `${tally.created} new`, tone: 'created' });
  if (tally.overwritten)
    chips.push({ text: `${tally.overwritten} overwrite`, tone: 'overwritten' });
  if (tally.skipped)
    chips.push({ text: `${tally.skipped} unchanged`, tone: 'skipped' });
  return chips;
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  > h2 {
    font-size: var(--text-lg);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    margin: 0;
  }
`;

const DropArea = styled.label<{ $dragging: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 24px;
  border: 1.5px dashed
    ${p => (p.$dragging ? 'var(--primary)' : 'var(--border)')};
  border-radius: var(--radius-input);
  background: ${p =>
    p.$dragging
      ? 'color-mix(in srgb, var(--primary) 8%, transparent)'
      : 'var(--background)'};
  text-align: center;
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    background var(--transition-fast);

  &:hover {
    border-color: var(--primary);
  }

  > strong {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }

  > span {
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const IconCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  color: var(--primary);
  background: color-mix(in srgb, var(--primary) 12%, transparent);
`;

const FileLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--background);

  > .file {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: var(--primary);

    > strong {
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
      color: var(--foreground);
      overflow-wrap: anywhere;
    }
  }

  > .rows {
    flex-shrink: 0;
    font-size: var(--text-xs);
    color: var(--text-muted);
  }
`;

const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols ?? 2}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > label,
  > .labelRow > label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
  }

  > .labelRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--primary);

  &:hover {
    color: var(--primary-hover);
  }
`;

const NewCollectionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const Muted = styled.span`
  font-size: var(--text-sm);
  color: var(--text-muted);
`;

// "Detecting language…" indicator shown while a detection request is in flight
// (PLAT-10900), inline beside each of the Source and Target language labels. The
// trailing dots cycle 1 → 2 → 3 via a pure-CSS step animation.
const ellipsis = keyframes`
  0% { content: '.'; }
  33% { content: '..'; }
  66%, 100% { content: '...'; }
`;

// While detection is in flight, the label reads yellow (gold) to signal work in
// progress; it settles to the grey "Identified …" hint once it resolves.
const DetectingHint = styled.span`
  font-size: var(--text-xs);
  color: var(--accent);
`;

// Resolved detection note beside a language label (PLAT-10900), in muted grey:
// either "Identified <language>" or "Language not identified", each with an info
// icon carrying the longer explanation. The unavailable case reads gold instead
// — see `UnavailableHint`.
const DetectedHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const InfoAnchor = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
  color: var(--text-muted);

  &:hover {
    color: var(--text-secondary);
  }
`;

// Field label plus an inline trailing hint on the same line.
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  > label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
  }
`;

const Dots = styled.span`
  &::after {
    content: '...';
    animation: ${ellipsis} 1.2s steps(1, end) infinite;
  }
`;

// Detected a language the workspace doesn't have (PLAT-10900). Shares the
// detection slot beside the field label with DetectedHint, but reads gold —
// there is nothing to select, so the only way forward is adding it in Brand Kit.
const UnavailableHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--text-xs);
  color: var(--accent);

  /* The info icon reads gold with the rest of the hint, not muted grey. */
  > span {
    color: inherit;
  }

  > button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: inherit;
    text-decoration: underline;
  }
`;

// Non-blocking language-mismatch hint under a language Select (PLAT-10900):
// gold (advisory, not an error) with an inline "Use <detected>" action.
const WarnLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
  font-size: var(--text-xs);
  color: var(--accent);

  > span > strong {
    font-weight: var(--font-weight-semi-bold);
  }

  > button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--primary);
    text-decoration: underline;
  }
`;

// The same non-blocking mismatch, carried onto the preview as a banner.
const WarnBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid var(--accent);
  border-radius: var(--radius-input);
  font-size: var(--text-sm);
  color: var(--foreground);
  background: color-mix(in srgb, var(--accent) 8%, transparent);

  strong {
    font-weight: var(--font-weight-semi-bold);
  }

  > button {
    align-self: flex-start;
    margin-top: 4px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--primary);
    text-decoration: underline;
  }
`;

// Reminds the reviewer where these rows will land before they confirm (the
// target collection was chosen a step earlier, on the configure screen).
const TargetLine = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);

  > strong {
    color: var(--foreground);
    font-weight: var(--font-weight-medium);
  }
`;

const CountRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Chip = styled.span<{ $tone: ChipTone }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  ${p => {
    const tone = {
      created: 'var(--primary)',
      overwritten: 'var(--accent)',
      skipped: 'var(--text-muted)',
      invalid: 'var(--destructive)',
    }[p.$tone];
    return `color: ${tone}; background: color-mix(in srgb, ${tone} 14%, transparent);`;
  }}
`;

const RowScroll = styled.div`
  overflow: auto;
  max-height: 40vh;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const PreviewToolbar = styled.div`
  display: flex;
  gap: 12px;

  > input {
    flex: 1;
  }

  > select {
    flex-shrink: 0;
    width: auto;
    min-width: 160px;
  }
`;

const PreviewTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: var(--text-xs);

  > thead th {
    position: sticky;
    top: 0;
    background: var(--background);
    text-align: left;
    padding: 6px 10px;
    color: var(--text-muted);
    box-shadow: inset 0 -1px 0 var(--border);
    white-space: nowrap;
  }

  > tbody td {
    padding: 6px 10px;
    border-bottom: 1px solid var(--border);
    color: var(--foreground);
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  .num {
    color: var(--text-muted);
    white-space: nowrap;
  }

  .empty {
    padding: 24px 10px;
    text-align: center;
    color: var(--text-secondary);
  }
`;

const ErrorBox = styled.div`
  padding: 10px 12px;
  border: 1px solid var(--destructive);
  border-radius: var(--radius-input);
  font-size: var(--text-sm);
  color: var(--foreground);

  > ul {
    margin: 6px 0 0;
    padding-left: 18px;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  color: var(--destructive);
`;
